import { Link } from '@tanstack/react-router';

import type { WrongInvestmentTypeErrorResponse } from '#/api/generated/fINRAMarketDataAPI.schemas';
import { ContentLayout } from '#/components/layout/ContentLayout';
import { BrandAccent } from '#/components/ui/BrandAccent';
import { Button } from '#/components/ui/Button';
import { NotFound } from '#/components/ui/NotFound';
import { isWrongInvestmentTypeError } from '#/lib/api-client';
import {
  getDetailHrefForInvestmentType,
  getInvestmentTypeLabel,
  getProductTypeSlugForInvestmentType,
} from '#/lib/category-navigation';

interface DetailNotFoundStateProps {
  symbol: string;
  error: unknown;
}

function WrongInvestmentTypeSuggestion({
  error,
}: {
  error: WrongInvestmentTypeErrorResponse;
}) {
  const actualType = error.actual_investment_type;

  if (!actualType) {
    return (
      <NotFound
        message={`"${error.symbol}" belongs to a different investment type.`}
      />
    );
  }

  const detailHref = getDetailHrefForInvestmentType(actualType, error.symbol);
  const productTypeSlug = getProductTypeSlugForInvestmentType(actualType);
  const investmentTypeLabel = getInvestmentTypeLabel(actualType);

  return (
    <div className="flow py-16">
      <BrandAccent variant="long" />
      <h1 className="text-static-60 text-fg-header leading-none font-semibold">
        404
      </h1>
      <p className="text-fg-utility">
        Did you mean the{' '}
        <Link
          className="text-link-fg underline underline-offset-2"
          to={detailHref}
        >
          {investmentTypeLabel} {error.symbol}
        </Link>
        ?
      </p>
      <Button
        variant="outline-primary"
        size="md"
        to={detailHref}
        suffixIcon="arrow-right"
      >
        View {productTypeSlug === 'etfs' ? 'ETF' : investmentTypeLabel}
      </Button>
    </div>
  );
}

export function DetailNotFoundState({
  symbol,
  error,
}: DetailNotFoundStateProps) {
  const content = isWrongInvestmentTypeError(error) ? (
    <WrongInvestmentTypeSuggestion error={error} />
  ) : (
    <NotFound message={`"${symbol}" not found.`} />
  );

  return (
    <ContentLayout>
      <div className="col-content">{content}</div>
    </ContentLayout>
  );
}
